import React, { useState, useCallback } from 'react';
import { Trash2, Sparkles, ArrowUp, ArrowDown } from 'lucide-react';
import { MemberListHeader } from './MemberListHeader';
import { MemberListItem } from './MemberListItem';
import { MemberDetailsModal } from './MemberDetailsModal';
import { AICreditsCompensation } from './credits/AICreditsCompensation';
import { PaymentHistoryDownload } from './PaymentHistoryDownload';
import { DateRangeSelector } from './DateRangeSelector';
import { useMemberStore } from '../../../stores/memberStore';
import { useAiCreditsStore } from '../../../stores/aiCreditsStore';
import { calculateCompensation } from '../../../utils/aiCredits/compensation';
import type { MemberExtends } from '../../../types/member';

// Extend the Window type to include memberDeletionTimeouts
declare global {
  interface Window {
    memberDeletionTimeouts?: { [memberId: string]: ReturnType<typeof setTimeout> };
  }
}

type SortColumn = 'memberNumber' | 'email' | 'plan' | 'registeredAt' | 'aiCredits' | 'referralCode' | 'salesAmount' | 'status' | 'role';
type SortDirection = 'asc' | 'desc';

export function MemberList() {
  const [selectedMembers, setSelectedMembers] = useState<Set<string>>(new Set());
  const [selectedMember, setSelectedMember] = useState<MemberExtends | null>(null);
  const [showCompensation, setShowCompensation] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sortColumn, setSortColumn] = useState<SortColumn>('registeredAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  
  const { users, updateMember, deleteMember } = useMemberStore();
  const { addChargedCreditsForUser } = useAiCreditsStore();

  // Sort users based on current sort column and direction
  const sortedMembers = React.useMemo(() => {
    return [...users].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortColumn) {
        case 'memberNumber':
          aValue = a.memberNumber;
          bValue = b.memberNumber;
          break;
        case 'email':
          aValue = a.email;
          bValue = b.email;
          break;
        case 'plan':
          aValue = a.plan;
          bValue = b.plan;
          break;
        case 'registeredAt':
          aValue = new Date(a.registeredAt);
          bValue = new Date(b.registeredAt);
          break;
        case 'aiCredits':
          aValue = a.aiCredits;
          bValue = b.aiCredits;
          break;
        case 'referralCode':
          aValue = a.referralCode || '';
          bValue = b.referralCode || '';
          break;
        case 'salesAmount':
          aValue = a.salesAmount;
          bValue = b.salesAmount;
          break;
        case 'status':
          aValue = a.status;
          bValue = b.status;
          break;
        case 'role':
          aValue = a.role || 'user';
          bValue = b.role || 'user';
          break;
        default:
          return 0;
      }

      // Handle different data types
      if (aValue instanceof Date && bValue instanceof Date) {
        return sortDirection === 'asc' 
          ? aValue.getTime() - bValue.getTime()
          : bValue.getTime() - aValue.getTime();
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }

      // String comparison
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      
      if (sortDirection === 'asc') {
        return aStr.localeCompare(bStr);
      } else {
        return bStr.localeCompare(aStr);
      }
    });
  }, [users, sortColumn, sortDirection]);

  const handleSort = useCallback((column: SortColumn) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  }, [sortColumn]);

  const handleSelectAll = useCallback((selected: boolean) => {
    if (selected) {
      setSelectedMembers(new Set(sortedMembers.map(m => m.id)));
    } else {
      setSelectedMembers(new Set());
    }
  }, [sortedMembers]);

  const handleSelectMember = useCallback((memberId: string, selected: boolean) => {
    setSelectedMembers(prev => {
      const newSet = new Set(prev);
      if (selected) {
        newSet.add(memberId);
      } else {
        newSet.delete(memberId);
      }
      return newSet;
    });
  }, []);

  const handleCompensate = async (amount: number) => {
    try {
      // Process each selected member
      for (const memberId of Array.from(selectedMembers)) {
        const member = sortedMembers.find(m => m.id === memberId);
        if (member) {
          // Calculate compensation amount
          const result = calculateCompensation(member, amount, true);
          
          // Update user_credits table directly
          await addChargedCreditsForUser(memberId, amount);
          
          // Also update the local state for immediate UI feedback
          updateMember(memberId, { aiCredits: result.newCredits });
        }
      }
    } catch (error) {
      console.error('Error compensating AI credits:', error);
    }
    setShowCompensation(false);
    setSelectedMembers(new Set());
  };

  const handleDateRangeChange = (newStartDate: string, newEndDate: string) => {
    setStartDate(newStartDate);
    setEndDate(newEndDate);
  };

  const handleDeleteMember = (memberId: string) => {
    const deletionTime = new Date(Date.now() + 24 * 60 * 60 * 1000); // 24 hours from now
    updateMember(memberId, { 
      status: 'suspended',
      scheduledDeletionTime: deletionTime.toISOString()
    });

    // Schedule actual deletion
    const timeoutId = setTimeout(() => {
      const member = sortedMembers.find(m => m.id === memberId);
      if (member?.scheduledDeletionTime === deletionTime.toISOString()) {
        deleteMember(memberId);
      }
    }, 24 * 60 * 60 * 1000);

    // Store timeout ID for potential cancellation
    window.memberDeletionTimeouts = window.memberDeletionTimeouts || {};
    window.memberDeletionTimeouts[memberId] = timeoutId;
  };

  const handleCancelDeletion = (memberId: string) => {
    // Clear deletion timeout
    if (window.memberDeletionTimeouts?.[memberId]) {
      clearTimeout(window.memberDeletionTimeouts[memberId]);
      delete window.memberDeletionTimeouts[memberId];
    }

    // Update member status
    updateMember(memberId, {
      status: 'active',
      scheduledDeletionTime: undefined
    });
  };

  const selectedMembersList = sortedMembers.filter(m => selectedMembers.has(m.id));

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-8">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={selectedMembers.size === sortedMembers.length && sortedMembers.length > 0}
                onChange={(e) => handleSelectAll(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                disabled={sortedMembers.length === 0}
              />
              <span className="ml-2 text-sm text-gray-700">
                全て選択 ({selectedMembers.size}/{sortedMembers.length})
              </span>
            </label>
            <DateRangeSelector
              startDate={startDate}
              endDate={endDate}
              onChange={handleDateRangeChange}
            />
          </div>
          {selectedMembers.size > 0 && (
            <div className="flex items-center gap-4">
              <PaymentHistoryDownload 
                selectedMembers={selectedMembersList}
                startDate={startDate}
                endDate={endDate}
              />
              <button
                onClick={() => setShowCompensation(true)}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
              >
                <Sparkles className="w-4 h-4" />
                <span>AIクレジットを補償</span>
              </button>
            </div>
          )}
        </div>
      </div>

      <MemberListHeader 
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSort={handleSort}
      />
      
      {sortedMembers.length === 0 ? (
        <div className="p-8 text-center">
          <p className="text-gray-500">登録されている会員はいません</p>
        </div>
      ) : (
        <div className="divide-y divide-gray-200">
          {sortedMembers.map(member => (
            <MemberListItem
              key={member.id}
              member={member}
              isSelected={selectedMembers.has(member.id)}
              onSelect={(checked) => handleSelectMember(member.id, checked)}
              onUpdateMember={updateMember}
              onDeleteMember={handleDeleteMember}
              onCancelDeletion={handleCancelDeletion}
              startDate={startDate}
              endDate={endDate}
            />
          ))}
        </div>
      )}

      {selectedMember && (
        <MemberDetailsModal
          member={selectedMember}
          onClose={() => setSelectedMember(null)}
          onCompensate={(amount) => {
            handleCompensate(amount);
            setSelectedMember(null);
          }}
        />
      )}

      {showCompensation && (
        <AICreditsCompensation
          selectedCount={selectedMembers.size}
          onCompensate={handleCompensate}
          onClose={() => setShowCompensation(false)}
        />
      )}
    </div>
  );
}